/**
 * API Configuration for Article Affiliate Patroller
 */

// API Base URL - points to the FastAPI backend
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

// API Endpoints
export const API_ENDPOINTS = {
    // Dashboard
    DASHBOARD_STATS: '/dashboard/stats',
    DASHBOARD_ACTIVITIES: '/dashboard/activities',

    // Upload
    UPLOAD_URLS: '/upload/urls',

    // Jobs
    JOBS: '/jobs',
    JOB_ACTIONS: (jobId: string) => `/jobs/${jobId}/actions`,
    URL_PROCESS_RESULTS: '/url-process-results',
    URL_ERROR_TRACEBACK: (jobId: string, urlId: string) =>
        `/jobs/${jobId}/urls/${urlId}/error-traceback`,

    // Violations
    VIOLATIONS: '/violations',
    VIOLATION_DETAILS: (violationId: string) => `/violations/${violationId}`,
    UPDATE_VIOLATION_STATUS: (violationId: string) => `/violations/${violationId}/status`,
    VIOLATION_STATS: '/violations/stats',

    // Violation Export
    EXPORT_VIOLATIONS_BATCH: '/violations/export-batch',
    EXPORT_VIOLATIONS_PROGRESS: (taskId: string) => `/violations/export-progress/${taskId}`,
    EXPORT_VIOLATIONS_DOWNLOAD: (taskId: string) => `/violations/export-download/${taskId}`,
    EXPORT_VIOLATIONS_CANCEL: (taskId: string) => `/violations/export-cancel/${taskId}`,

    // Violation
    VIOLATIONS_FOR_REVIEW: (jobId: string, urlId: string) => `/violation/${jobId}/${urlId}/violations`,
    CREATE_VIOLATION: (jobId: string, urlId: string) => `/violation/${jobId}/${urlId}/violations`,
    UPDATE_VIOLATION: (jobId: string, urlId: string, violationId: string) => `/violation/${jobId}/${urlId}/violations/${violationId}`,
    DELETE_VIOLATION: (jobId: string, urlId: string, violationId: string) => `/violation/${jobId}/${urlId}/violations/${violationId}`,
    BULK_SAVE_VIOLATIONS: (jobId: string, urlId: string) => `/violation/${jobId}/${urlId}/violations/bulk-save`,
    MARK_REVIEWED: (jobId: string, urlId: string) => `/violation/${jobId}/${urlId}/mark-reviewed`,
    EXPORT_PDF: (jobId: string, urlId: string) => `/violation/${jobId}/${urlId}/export-pdf`,

    // Guidelines
    GUIDELINES: '/guidelines',
    GUIDELINE: (guidelineId: string) => `/guidelines/${guidelineId}`,
    GUIDELINE_VERSIONS: (guidelineId: string) => `/guidelines/${guidelineId}/versions`,
    GUIDELINE_UNFIXABLE_RULES_EXPORT: (guidelineId: string, version?: number) =>
        version != null
            ? `/guidelines/${guidelineId}/unfixable-rules/export?version=${version}`
            : `/guidelines/${guidelineId}/unfixable-rules/export`,
    UPDATE_GUIDELINE: (guidelineId: string) => `/guidelines/${guidelineId}`,
    UPDATE_GUIDELINE_YAML: (guidelineId: string) => `/guidelines/${guidelineId}/yaml`,
    DELETE_GUIDELINE: (guidelineId: string) => `/guidelines/${guidelineId}`,
    GUIDELINE_ASSET: '/guidelines/assets',

    // LLM Usage (monitoring dashboard)
    LLM_USAGE: '/llm-usage',
    LLM_USAGE_FILTER_OPTIONS: '/llm-usage/filter-options',
    LLM_USAGE_TIMESERIES: '/llm-usage/timeseries',
    LLM_USAGE_DETAIL: (usageId: string) => `/llm-usage/${usageId}`,

    // Accuracy monitoring dashboard (issue #225)
    ACCURACY_MONITORING_TIMESERIES: '/accuracy/monitoring/timeseries',
    ACCURACY_MONITORING_JOBS: '/accuracy/monitoring/jobs',
    ACCURACY_MONITORING_EXPORT: '/accuracy/monitoring/export',

    // Page status dashboard (error / no-ad-notation counts by month)
    STATUS_DASHBOARD_TIMESERIES: '/status-dashboard/timeseries',

    // Violation trend dashboard (patrolled/violation URL counts and rate over time)
    VIOLATION_TREND_TIMESERIES: '/violation-trend/timeseries',
} as const;

// HTTP Methods
export const HTTP_METHODS = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
} as const;

// Request configuration
export const REQUEST_CONFIG = {
    timeout: 300000, // 5 minutes
    headers: {
        'Content-Type': 'application/json',
        // CSRF protection: Browsers cannot set custom headers in cross-origin requests
        // without CORS preflight, which prevents CSRF attacks
        'X-Requested-With': 'XMLHttpRequest',
    },
} as const;

// Error messages
export const ERROR_MESSAGES = {
    NETWORK_ERROR: 'Network error. Please check your connection.',
    TIMEOUT_ERROR: 'Request timeout. Please try again.',
    SERVER_ERROR: 'Server error. Please try again later.',
    UNAUTHORIZED: 'Unauthorized access.',
    NOT_FOUND: 'Resource not found.',
    VALIDATION_ERROR: 'Validation error.',
} as const;
